import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useReducedMotionSafe } from "@/components/animation/AnimationSettings";
import { usePersonalization } from "@/components/personalization/PersonalizationContext";
import ComplianceLock from "@/components/compliance/ComplianceLock";

/**
 * UserPreferenceSetup — inline / modal preference editor.
 *
 * Lets an authenticated member configure their preferred markets, regions,
 * risk tolerance, time horizon, briefing style, voice + image-search toggles,
 * and watchlist. Strictly additive; never affects unauthenticated users.
 */

const PALETTE = {
  gold: "#c5a059",
  goldBright: "#fde68a",
  goldGlow: "rgba(197,160,89,0.45)",
  slate3: "#cbd5e1",
  slate4: "#94a3b8",
  slate5: "#64748b",
  glassDark: "rgba(11,15,20,0.95)",
  greenAccent: "#10b981",
};

function ChipButton({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={!!active}
      style={{
        appearance: "none",
        padding: "5px 11px",
        border: "1px solid",
        borderColor: active ? PALETTE.greenAccent : PALETTE.goldGlow,
        background: active ? "rgba(16,185,129,0.10)" : "transparent",
        color: active ? PALETTE.greenAccent : PALETTE.gold,
        borderRadius: 999,
        fontFamily: "'Space Mono', monospace",
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: "0.06em",
        cursor: "pointer",
        transition: "border-color 160ms ease, color 160ms ease",
      }}
    >
      {children}
    </button>
  );
}

function Field({ label, children, testid }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }} data-testid={testid}>
      <span style={{ color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.16em" }}>{label}</span>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>{children}</div>
    </div>
  );
}

export default function UserPreferenceSetup({
  asModal = false,
  open: openProp,
  onClose,
  testid = "user-preference-setup",
}) {
  const reduce = useReducedMotionSafe();
  const { profile, options, patch, isAuthed, loading, err } = usePersonalization();
  const [open, setOpen] = useState(!!openProp);
  const [pm, setPm] = useState([]);
  const [pr, setPr] = useState([]);
  const [risk, setRisk] = useState("moderate");
  const [horizon, setHorizon] = useState("swing");
  const [briefing, setBriefing] = useState("balanced");
  const [voice, setVoice] = useState(true);
  const [imgSearch, setImgSearch] = useState(true);
  const [wl, setWl] = useState("");
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState("");

  useEffect(() => { setOpen(!!openProp); }, [openProp]);
  useEffect(() => {
    if (!profile) return;
    setPm(profile.preferred_markets   || []);
    setPr(profile.preferred_regions   || []);
    setRisk(profile.risk_tolerance    || "moderate");
    setHorizon(profile.time_horizon   || "swing");
    setBriefing(profile.briefing_style|| "balanced");
    setVoice(profile.voice_enabled !== false);
    setImgSearch(profile.image_search_enabled !== false);
    setWl((profile.watchlist || []).join(", "));
  }, [profile]);

  const close = useCallback(() => {
    setOpen(false);
    if (typeof onClose === "function") onClose();
  }, [onClose]);

  const toggle = (arr, setter, v) =>
    setter((arr || []).includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const save = useCallback(async () => {
    setBusy(true); setOk("");
    try {
      const watchlist = wl.split(",").map((x) => x.trim()).filter(Boolean).slice(0, 60);
      const res = await patch({
        preferred_markets:    pm,
        preferred_regions:    pr,
        risk_tolerance:       risk,
        time_horizon:         horizon,
        briefing_style:       briefing,
        voice_enabled:        voice,
        image_search_enabled: imgSearch,
        watchlist,
      });
      if (res) {
        setOk("Saved. LOGIX-2 will adapt accordingly.");
        setTimeout(() => setOk(""), 2200);
      }
    } finally { setBusy(false); }
  }, [pm, pr, risk, horizon, briefing, voice, imgSearch, wl, patch]);

  const inner = useMemo(() => (
    <section style={S.shell} data-testid={testid}>
      <ComplianceLock variant="banner" policy="v27" testid={`${testid}-compliance-top`} />
      <header style={S.head}>
        <div>
          <span style={S.tagline}>Phase 31 · ORACLE Personalization Engine™</span>
          <h3 style={S.title}>Tune your intelligence companion</h3>
        </div>
        {asModal && (
          <button type="button" onClick={close} style={S.closeBtn} data-testid={`${testid}-close`}>✕</button>
        )}
      </header>

      {err && <div style={S.err}>⚠ {err}</div>}
      {ok  && <div style={S.ok}>✓ {ok}</div>}
      {!isAuthed && (
        <div style={S.warn}>Sign in to personalize your dashboard. Guests see the default ORACLE experience.</div>
      )}

      <Field label="Preferred markets" testid={`${testid}-markets`}>
        {(options?.markets || []).map((m) => (
          <ChipButton key={m} active={pm.includes(m)} onClick={() => toggle(pm, setPm, m)} testid={`${testid}-market-${m}`}>{m.toUpperCase()}</ChipButton>
        ))}
      </Field>

      <Field label="Preferred regions" testid={`${testid}-regions`}>
        {(options?.regions || []).map((r) => (
          <ChipButton key={r} active={pr.includes(r)} onClick={() => toggle(pr, setPr, r)} testid={`${testid}-region-${r.replace(/[^a-z0-9]+/gi, '-')}`}>{r}</ChipButton>
        ))}
      </Field>

      <Field label="Risk tolerance" testid={`${testid}-risk`}>
        {(options?.risk_tolerances || []).map((r) => (
          <ChipButton key={r} active={risk === r} onClick={() => setRisk(r)} testid={`${testid}-risk-${r}`}>{r}</ChipButton>
        ))}
      </Field>

      <Field label="Time horizon" testid={`${testid}-horizon`}>
        {(options?.time_horizons || []).map((h) => (
          <ChipButton key={h} active={horizon === h} onClick={() => setHorizon(h)} testid={`${testid}-horizon-${h}`}>{h.replace("_"," ")}</ChipButton>
        ))}
      </Field>

      <Field label="Briefing style" testid={`${testid}-briefing`}>
        {(options?.briefing_styles || []).map((b) => (
          <ChipButton key={b} active={briefing === b} onClick={() => setBriefing(b)} testid={`${testid}-briefing-${b}`}>{b.replace("_"," ")}</ChipButton>
        ))}
      </Field>

      <Field label="Companion features" testid={`${testid}-toggles`}>
        <ChipButton active={voice} onClick={() => setVoice((v) => !v)} testid={`${testid}-voice`}>Voice {voice ? "ON" : "OFF"}</ChipButton>
        <ChipButton active={imgSearch} onClick={() => setImgSearch((v) => !v)} testid={`${testid}-image-search`}>Image search {imgSearch ? "ON" : "OFF"}</ChipButton>
      </Field>

      <div style={S.wlWrap}>
        <label style={S.wlLabel} htmlFor={`${testid}-watchlist-input`}>Watchlist (comma-separated, e.g. NVDA, BTC, EUR/USD)</label>
        <input
          id={`${testid}-watchlist-input`}
          data-testid={`${testid}-watchlist-input`}
          value={wl}
          onChange={(e) => setWl(e.target.value)}
          placeholder="AAPL, BTC, EUR/USD"
          style={S.wlInput}
        />
      </div>

      <footer style={S.foot}>
        <button type="button" onClick={save} disabled={busy || loading || !isAuthed} style={S.saveBtn} data-testid={`${testid}-save`}>
          {busy || loading ? "Saving…" : "Save preferences"}
        </button>
        <span style={S.compliance}>{options?.disclaimer || "Personalization is decision-support only — not financial advice."}</span>
      </footer>

      <ComplianceLock variant="inline" short policy="v27" testid={`${testid}-compliance-footer`} />
    </section>
  ), [pm, pr, risk, horizon, briefing, voice, imgSearch, wl, busy, loading, isAuthed, options, err, ok, asModal, close, save, testid]);

  if (!asModal) return inner;
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          style={S.backdrop}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={reduce ? { duration: 0 } : { duration: 0.2 }}
          onClick={close}
          data-testid={`${testid}-backdrop`}
          role="dialog"
          aria-modal="true"
        >
          <motion.div
            style={S.modalCard}
            initial={{ y: 18, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 10, opacity: 0 }}
            transition={reduce ? { duration: 0 } : { duration: 0.28, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
          >
            {inner}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const S = {
  shell: { background: "linear-gradient(135deg, rgba(11,15,20,0.95), rgba(8,32,22,0.93))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 14, padding: 14, color: PALETTE.slate3, display: "flex", flexDirection: "column", gap: 12, fontFamily: "'Figtree', system-ui, sans-serif", maxWidth: "100%", boxSizing: "border-box" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  tagline: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.18em" },
  title: { color: PALETTE.goldBright, margin: 0, fontSize: 16 },
  closeBtn: { appearance: "none", background: "transparent", border: `1px solid ${PALETTE.goldGlow}`, color: PALETTE.gold, borderRadius: 8, padding: "4px 10px", fontFamily: "'Space Mono', monospace", cursor: "pointer" },
  err: { padding: "8px 12px", background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.30)", color: "#fecaca", borderRadius: 10, fontSize: 12 },
  ok:  { padding: "8px 12px", background: "rgba(16,185,129,0.10)", border: "1px solid rgba(16,185,129,0.30)", color: "#d1fae5", borderRadius: 10, fontSize: 12 },
  warn:{ padding: "8px 12px", background: "rgba(252,211,77,0.10)", border: "1px solid rgba(252,211,77,0.30)", color: "#fde68a", borderRadius: 10, fontSize: 12 },
  wlWrap: { display: "flex", flexDirection: "column", gap: 4 },
  wlLabel: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.16em" },
  wlInput: { appearance: "none", background: "#040608", border: "1px solid rgba(255,255,255,0.10)", color: PALETTE.slate3, padding: "8px 10px", borderRadius: 10, fontSize: 12.5, fontFamily: "'Figtree', system-ui, sans-serif" },
  foot: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  saveBtn: { appearance: "none", background: "linear-gradient(135deg, #c5a059, #ffd966)", border: "1px solid #c5a059", color: "#0c1a10", padding: "9px 16px", borderRadius: 10, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11.5, fontWeight: 800, letterSpacing: "0.06em" },
  compliance: { color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 10 },
  backdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.62)", zIndex: 70, display: "flex", justifyContent: "center", alignItems: "center", padding: 16, backdropFilter: "blur(6px)" },
  modalCard: { width: "min(900px, 100%)", maxHeight: "90vh", overflow: "auto" },
};
